import React from 'react';

import { Trans } from 'react-i18next';
import ReactTooltip from 'react-tooltip';

import { CustomModal, Icon, Select, UiButton, UiText } from '@shared/ui';

import { FileDropInput } from './FileDropInput';
import cls from './ImportModal.module.css';
import { useImportModal } from './useImportModal';

interface ImportModalProps {
    isPending: boolean;
    isShow: boolean;
    onClose: () => void;
    handleSubmit: (account: string, bank: string, file: File) => Promise<void>;
}

export const ImportModal = ({ onClose, isShow, handleSubmit, isPending }: ImportModalProps) => {
    const {
        t,
        importForm,
        accountsList,
        onAccountSelect,
        banksList,
        onBankSelect,
        handleFile,
        fileError,
        addAccountOption,
        noAppropriateAccounts,
    } = useImportModal();

    return (
        <CustomModal onHideModal={onClose} isOpen={isShow} isLoading={isPending}>
            <CustomModal.Header>
                <div className={cls.ImportModalHeader}>
                    {t('ImportModal.return.CustomModal.CustomModal.Header')}
                    <ReactTooltip
                        id="ExportPdf"
                        aria-haspopup="true"
                        effect="solid"
                        delayHide={200}
                        delayShow={200}
                        type="light"
                        className={cls.tooltip}
                    >
                        <p>
                            <Trans i18nKey="ImportModal.return.CustomModal.Header.ReactTooltip" />
                        </p>
                    </ReactTooltip>
                    <span className={cls.question} data-tip data-for="ExportPdf">
                        <Icon iconName="question" fill="currentColor" />
                    </span>
                </div>
            </CustomModal.Header>
            <CustomModal.Body>
                <div className={cls.importBody}>
                    <Select
                        isSmartPosition
                        placeholder={t(
                            'ImportModal.return.CustomModal.CustomModal.Body.Select.account.placeholder'
                        )}
                        label={t(
                            'ImportModal.return.CustomModal.CustomModal.Body.Select.account.label'
                        )}
                        classes={cls.selectWrapper}
                        selectRootClassName={cls.SelectRoot}
                        value={importForm.account}
                        options={accountsList}
                        onChange={onAccountSelect}
                        TextBeforeListEl={
                            noAppropriateAccounts &&
                            accountsList[0]?.value === 'addAccountOption' && (
                                <UiText tag="span" classes={cls.textBeforeListEl}>
                                    {t(
                                        'ImportModal.return.CustomModal.CustomModal.Body.Select.account.noAppropriateAccounts.text'
                                    )}
                                </UiText>
                            )
                        }
                    />
                    <Select
                        isSmartPosition
                        placeholder={t(
                            'ImportModal.return.CustomModal.CustomModal.Body.Select.bank.placeholder'
                        )}
                        label={t(
                            'ImportModal.return.CustomModal.CustomModal.Body.Select.bank.label'
                        )}
                        options={banksList}
                        onChange={onBankSelect}
                        value={importForm.bank}
                        selectRootClassName={cls.SelectRoot}
                        classes={cls.selectWrapper}
                    />
                    <FileDropInput
                        onFileSelect={handleFile}
                        file={importForm.file}
                        error={fileError}
                    />
                </div>
            </CustomModal.Body>
            <CustomModal.Footer>
                <div className={cls.footer}>
                    <UiButton
                        btnColor="secondary"
                        btnText={t('button.cancel.text')}
                        onClick={onClose}
                    />
                    <UiButton
                        onClick={() =>
                            handleSubmit(importForm.account, importForm.bank, importForm.file)
                        }
                        disabled={
                            !importForm.bank.length ||
                            !importForm.account.length ||
                            !importForm.file
                        }
                        btnText={t('button.importing.text')}
                    />
                </div>
            </CustomModal.Footer>
        </CustomModal>
    );
};
